"use client";

import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ArrowLeft, Crown } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

interface DiscountConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDiscount: number;
  currentTotal: number;
  newTotal: number;
  /** Platinum member discount (auto-calculated, 15% of newTotal) */
  platinumDiscount?: number;
  onConfirmWithAdjustment: () => void;
  onConfirmWithNewDiscount: (discount: number) => void;
  onCancel: () => void;
}

export function DiscountConfirmationDialog({
  open,
  onOpenChange,
  currentDiscount,
  currentTotal,
  newTotal,
  platinumDiscount = 0,
  onConfirmWithAdjustment,
  onConfirmWithNewDiscount,
  onCancel,
}: DiscountConfirmationDialogProps) {
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [customDiscount, setCustomDiscount] = useState<string>("");
  const [discountError, setDiscountError] = useState<string>("");

  // Maximum manual discount is newTotal minus platinumDiscount
  const maxManualDiscount = Math.max(0, newTotal - platinumDiscount);

  // Calculate if discount needs adjustment (considering platinum discount)
  const discountNeedsAdjustment = currentDiscount > maxManualDiscount;
  const adjustedDiscount = Math.min(currentDiscount, maxManualDiscount);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setShowDiscountInput(false);
      setCustomDiscount(adjustedDiscount.toString());
      setDiscountError("");
    }
  }, [open, adjustedDiscount]);

  const handleDiscountChange = (value: string) => {
    setCustomDiscount(value);
    setDiscountError("");

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      setDiscountError("Discount must be a positive number");
    } else if (numValue > maxManualDiscount) {
      setDiscountError(`Discount cannot exceed ${formatCurrency(maxManualDiscount)}`);
    }
  };

  const handleApplyCustomDiscount = () => {
    const numValue = parseFloat(customDiscount);
    if (isNaN(numValue) || numValue < 0 || numValue > maxManualDiscount) {
      setDiscountError(
        `Enter a valid discount (0 - ${formatCurrency(maxManualDiscount)})`,
      );
      return;
    }
    onConfirmWithNewDiscount(numValue);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  if (showDiscountInput) {
    // Second screen: Custom discount input
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enter New Discount</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">New Total:</span>
                    <span className="font-semibold">
                      {formatCurrency(newTotal)}
                    </span>
                  </div>
                  {platinumDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Crown className="h-3 w-3 text-amber-500" />
                        Platinum Discount (15%):
                      </span>
                      <span className="font-semibold text-amber-600">
                        -{formatCurrency(platinumDiscount)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-discount">Manual Discount Amount (AED)</Label>
                  <Input
                    id="custom-discount"
                    type="number"
                    min="0"
                    max={maxManualDiscount}
                    step="0.01"
                    value={customDiscount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    placeholder="Enter discount amount"
                    className={discountError ? "border-destructive" : ""}
                  />
                  {discountError && (
                    <p className="text-sm text-destructive">{discountError}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Maximum manual discount: {formatCurrency(maxManualDiscount)}
                    {platinumDiscount > 0 && " (after platinum discount)"}
                  </p>
                </div>

                {customDiscount && !discountError && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Final Due Amount:
                      </span>
                      <span className="font-semibold text-green-700 dark:text-green-400">
                        {formatCurrency(
                          newTotal - platinumDiscount - (parseFloat(customDiscount) || 0),
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDiscountInput(false)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex-1" />
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleApplyCustomDiscount}
              disabled={!!discountError}
            >
              Apply & Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // First screen: Confirmation with options
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {discountNeedsAdjustment ? (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Discount Adjustment Required
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-blue-500" />
                Confirm Vehicle Changes
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {discountNeedsAdjustment ? (
                <p>
                  Changing vehicles will reduce the total amount below the
                  current discount. The discount will be automatically adjusted.
                </p>
              ) : (
                <p>
                  This appointment has a discount applied. Review the changes
                  below.
                </p>
              )}

              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Total:</span>
                  <span className="font-medium">
                    {formatCurrency(currentTotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">New Total:</span>
                  <span className="font-medium">
                    {formatCurrency(newTotal)}
                  </span>
                </div>
                <div className="border-t pt-2 mt-2 space-y-1">
                  {platinumDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Crown className="h-3 w-3 text-amber-500" />
                        Platinum Discount (15%):
                      </span>
                      <span className="font-medium text-amber-600">
                        -{formatCurrency(platinumDiscount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Current Manual Discount:
                    </span>
                    <span className="font-medium">
                      {formatCurrency(currentDiscount)}
                    </span>
                  </div>
                  {discountNeedsAdjustment && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-muted-foreground">
                        Adjusted Discount:
                      </span>
                      <span className="font-medium text-amber-600">
                        {formatCurrency(adjustedDiscount)} ⬅️ Will be capped
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                What would you like to do?
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
          <Button variant="outline" onClick={() => setShowDiscountInput(true)}>
            Update Manual Discount
          </Button>
          <AlertDialogAction onClick={onConfirmWithAdjustment}>
            {discountNeedsAdjustment ? "Proceed with Adjustment" : "Proceed"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
